<template>
  <div class="flex flex-col items-center justify-center p-2">
    <!-- Dropzone (When no files uploaded) -->
    <div v-if="files.length === 0" 
      @click="fileInput.click()"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="handleDrop"
      class="w-full max-w-4xl bg-gradient-to-b from-red-50 to-white hover:from-white hover:to-red-50 mt-5 transition-all duration-300 cursor-pointer shadow-lg rounded-2xl flex flex-col items-center justify-center text-center py-12 px-8 relative overflow-hidden border-2 border-dashed"
      :class="[isDragging ? 'border-red-500 bg-red-50 shadow-2xl' : 'border-red-400']">
      
      <div class="relative z-10 flex flex-col items-center">
        <div class="mx-auto w-20 h-20 flex items-center justify-center mb-4">
          <img src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/pdf%20%282%29-g2dPLzFcgQFJYtGGxU0T9PMNB8UPUx.png" alt="PDF Upload" class="w-full h-full object-contain" />
        </div>
        
        <!-- Upload Button -->
        <button
          @click.stop="fileInput.click()"
          class="bg-red-500 hover:bg-red-600 text-white font-semibold px-8 py-3 rounded-lg active:bg-red-700 transition-all duration-200 flex items-center gap-2 shadow-md text-base">
          <Upload :size="20" />
          <span class="font-semibold">Select PDF files</span>
        </button>
        
        <p class="mb-4 font-semibold text-base text-gray-700 mt-6">
          or drop PDFs here
        </p>
        <p class="text-sm text-gray-500 font-medium">
          or press <span class="bg-gray-200 px-2 py-1 rounded font-mono text-gray-800">CTRL + V</span> to paste
        </p>
      </div>
    </div>

    <!-- Compression Interface (When files uploaded) -->
    <div v-else class="bg-white rounded-2xl w-full max-w-4xl shadow-xl border border-gray-200 mt-4 overflow-hidden">
      <!-- Top Bar / File Header -->
      <div class="bg-gradient-to-r from-red-50 via-white to-red-50 p-5 border-b border-red-100 flex flex-wrap items-center justify-between gap-4">
        <div class="flex items-center gap-3">
          <div class="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center text-red-600">
            <FileText :size="24" />
          </div>
          <div>
            <h3 class="font-bold text-gray-900 text-lg flex items-center gap-2">
              <span>{{ files.length === 1 ? files[0].name : `${files.length} PDF files selected` }}</span>
            </h3>
            <p class="text-sm text-gray-500 font-medium">
              Total Original Size: <span class="font-bold text-gray-700">{{ formatBytes(totalOriginalSize) }}</span>
            </p>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <button @click="fileInput.click()" class="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 px-3.5 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5 transition shadow-sm">
            <Plus :size="16" />
            Add More
          </button>
          <button @click="resetAll" class="bg-gray-100 hover:bg-gray-200 text-gray-600 px-3.5 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5 transition">
            <RotateCcw :size="16" />
            Clear
          </button>
        </div>
      </div>

      <!-- Compression Options Section -->
      <div class="p-6 md:p-8">
        <h4 class="text-xl font-bold text-gray-900 mb-2 text-center md:text-left">
          Select Compression Level
        </h4>
        <p class="text-gray-600 text-sm mb-6 text-center md:text-left">
          Choose how much you want to compress your PDF document:
        </p>

        <!-- Compression Cards Grid -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          
          <!-- High Compression -->
          <div @click="compressionLevel = 'high'"
            class="relative p-5 rounded-xl border-2 cursor-pointer transition-all duration-200 flex flex-col justify-between"
            :class="[compressionLevel === 'high' ? 'border-red-500 bg-red-50/50 shadow-md ring-2 ring-red-500/20' : 'border-gray-200 hover:border-red-300 bg-white']">
            
            <div class="flex items-center justify-between mb-3">
              <span class="text-xs font-bold uppercase tracking-wider bg-red-100 text-red-700 px-2.5 py-1 rounded-full">Smallest File</span>
              <div class="w-5 h-5 rounded-full border-2 flex items-center justify-center"
                :class="[compressionLevel === 'high' ? 'border-red-600 bg-red-600' : 'border-gray-300']">
                <div v-if="compressionLevel === 'high'" class="w-2 h-2 rounded-full bg-white"></div>
              </div>
            </div>

            <h5 class="text-lg font-bold text-gray-900 mb-1">High compression</h5>
            <p class="text-xs text-gray-600 leading-relaxed mb-3">
              Smallest file size. Best when you just need to send or store the file and image quality isn't critical.
            </p>
            <div class="text-[11px] font-semibold text-red-600 bg-red-100/60 px-2 py-1 rounded w-fit">
              Max Compression (~60-80% smaller)
            </div>
          </div>

          <!-- Medium Compression -->
          <div @click="compressionLevel = 'medium'"
            class="relative p-5 rounded-xl border-2 cursor-pointer transition-all duration-200 flex flex-col justify-between"
            :class="[compressionLevel === 'medium' ? 'border-red-500 bg-red-50/50 shadow-md ring-2 ring-red-500/20' : 'border-gray-200 hover:border-red-300 bg-white']">
            
            <!-- Recommended Badge -->
            <div class="absolute -top-3 right-4 bg-red-500 text-white text-[10px] font-extrabold uppercase px-2.5 py-0.5 rounded-full shadow-sm">
              Recommended
            </div>

            <div class="flex items-center justify-between mb-3">
              <span class="text-xs font-bold uppercase tracking-wider bg-green-100 text-green-700 px-2.5 py-1 rounded-full">Balanced</span>
              <div class="w-5 h-5 rounded-full border-2 flex items-center justify-center"
                :class="[compressionLevel === 'medium' ? 'border-red-600 bg-red-600' : 'border-gray-300']">
                <div v-if="compressionLevel === 'medium'" class="w-2 h-2 rounded-full bg-white"></div>
              </div>
            </div>

            <h5 class="text-lg font-bold text-gray-900 mb-1">Medium compression</h5>
            <p class="text-xs text-gray-600 leading-relaxed mb-3">
              A balance between size and quality. Good for most everyday documents.
            </p>
            <div class="text-[11px] font-semibold text-green-700 bg-green-100/60 px-2 py-1 rounded w-fit">
              Good Balance (~40-60% smaller)
            </div>
          </div>

          <!-- Low Compression -->
          <div @click="compressionLevel = 'low'"
            class="relative p-5 rounded-xl border-2 cursor-pointer transition-all duration-200 flex flex-col justify-between"
            :class="[compressionLevel === 'low' ? 'border-red-500 bg-red-50/50 shadow-md ring-2 ring-red-500/20' : 'border-gray-200 hover:border-red-300 bg-white']">
            
            <div class="flex items-center justify-between mb-3">
              <span class="text-xs font-bold uppercase tracking-wider bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full">High Quality</span>
              <div class="w-5 h-5 rounded-full border-2 flex items-center justify-center"
                :class="[compressionLevel === 'low' ? 'border-red-600 bg-red-600' : 'border-gray-300']">
                <div v-if="compressionLevel === 'low'" class="w-2 h-2 rounded-full bg-white"></div>
              </div>
            </div>

            <h5 class="text-lg font-bold text-gray-900 mb-1">Low compression</h5>
            <p class="text-xs text-gray-600 leading-relaxed mb-3">
              Keeps quality close to the original. Best for files with detailed images or graphics you want to preserve.
            </p>
            <div class="text-[11px] font-semibold text-blue-700 bg-blue-100/60 px-2 py-1 rounded w-fit">
              High Quality (~20-40% smaller)
            </div>
          </div>

        </div>

        <!-- Result Card (If already compressed) -->
        <div v-if="compressedResult" class="mb-6 p-5 bg-green-50 border-2 border-green-300 rounded-xl flex flex-col md:flex-row items-center justify-between gap-4">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-full bg-green-500 text-white flex items-center justify-center font-bold text-xl">
              ✓
            </div>
            <div>
              <h5 class="font-bold text-green-900 text-base">PDF Compressed Successfully!</h5>
              <p class="text-xs text-green-700 font-medium">
                Original: <s>{{ formatBytes(compressedResult.originalSize) }}</s> → 
                Compressed: <strong class="text-green-900 font-bold">{{ formatBytes(compressedResult.compressedSize) }}</strong>
              </p>
            </div>
          </div>

          <div class="flex items-center gap-3">
            <span class="bg-green-600 text-white text-xs font-extrabold px-3 py-1.5 rounded-full shadow-sm">
              🎉 {{ compressedResult.savingPercent }}% Smaller
            </span>
            <button @click="downloadCompressedFile" class="bg-green-600 hover:bg-green-700 text-white font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-2 shadow-md transition">
              <Download :size="16" />
              Download
            </button>
          </div>
        </div>

        <!-- Action Button -->
        <div class="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            @click="compressFiles"
            :disabled="isProcessing"
            class="w-full sm:w-auto bg-red-500 hover:bg-red-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold px-10 py-3.5 rounded-xl shadow-lg hover:shadow-xl transition-all text-lg flex items-center justify-center gap-3">
            
            <div v-if="isProcessing" class="flex items-center gap-2">
              <div class="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
              <span>Compressing PDF ({{ progressPercent }}%)...</span>
            </div>

            <div v-else class="flex items-center gap-2">
              <Zap :size="22" />
              <span>Compress PDF</span>
            </div>
          </button>
        </div>

      </div>
    </div>

    <!-- Hidden File Input -->
    <input
      type="file"
      ref="fileInput"
      multiple
      accept="application/pdf"
      class="hidden"
      @change="onFileChange"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { PDFDocument } from 'pdf-lib'
import * as pdfjsLib from 'pdfjs-dist/build/pdf'
import { Upload, FileText, Plus, RotateCcw, Download, Zap } from 'lucide-vue-next'

// Worker configuration
pdfjsLib.GlobalWorkerOptions.workerSrc = '/js/pdf.worker.js'

const props = defineProps({
  translations: {
    type: Object,
    default: () => ({})
  }
})

// State
const files = ref([])
const fileInput = ref(null)
const isDragging = ref(false)
const compressionLevel = ref('medium') // default: 'medium'
const isProcessing = ref(false)
const progressPercent = ref(0)
const compressedResult = ref(null)

const totalOriginalSize = computed(() => {
  return files.value.reduce((acc, f) => acc + f.size, 0)
})

const handleDrop = (e) => {
  isDragging.value = false
  const droppedFiles = Array.from(e.dataTransfer.files).filter(f => f.type === 'application/pdf')
  if (droppedFiles.length) {
    addFiles(droppedFiles)
  }
}

const onFileChange = (e) => {
  const selectedFiles = Array.from(e.target.files).filter(f => f.type === 'application/pdf')
  if (selectedFiles.length) {
    addFiles(selectedFiles)
  }
  e.target.value = ''
}

const handlePaste = (e) => {
  const items = e.clipboardData?.items
  if (!items) return
  for (let item of items) {
    if (item.type === 'application/pdf') {
      const file = item.getAsFile()
      if (file) addFiles([file])
    }
  }
}

const addFiles = (newFiles) => {
  compressedResult.value = null
  for (const f of newFiles) {
    if (!files.value.some(existing => existing.name === f.name && existing.size === f.size)) {
      files.value.push(f)
    }
  }
}

const resetAll = () => {
  files.value = []
  compressedResult.value = null
  progressPercent.value = 0
  isProcessing.value = false
}

const formatBytes = (bytes, decimals = 2) => {
  if (!bytes || bytes === 0) return '0 Bytes'
  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

// Compression Logic
const compressFiles = async () => {
  if (files.value.length === 0) return

  isProcessing.value = true
  progressPercent.value = 5

  try {
    // Determine quality parameters based on level
    let renderScale = 1.1
    let jpegQuality = 0.68

    if (compressionLevel.value === 'high') {
      renderScale = 0.75
      jpegQuality = 0.45
    } else if (compressionLevel.value === 'low') {
      renderScale = 1.5
      jpegQuality = 0.85
    }

    const outputPdf = await PDFDocument.create()

    let totalPagesProcessed = 0
    let grandTotalPages = 0

    // Count total pages across files
    const parsedDocs = []
    for (const file of files.value) {
      const buffer = await file.arrayBuffer()
      const pdfjsDoc = await pdfjsLib.getDocument({ data: buffer }).promise
      parsedDocs.push({ file, pdfjsDoc })
      grandTotalPages += pdfjsDoc.numPages
    }

    // Process pages sequentially
    for (const { pdfjsDoc } of parsedDocs) {
      for (let pageNum = 1; pageNum <= pdfjsDoc.numPages; pageNum++) {
        const page = await pdfjsDoc.getPage(pageNum)
        const viewport = page.getViewport({ scale: renderScale })

        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        canvas.width = viewport.width
        canvas.height = viewport.height

        await page.render({ canvasContext: ctx, viewport }).promise

        const imgDataUrl = canvas.toDataURL('image/jpeg', jpegQuality)
        const imgBytes = await fetch(imgDataUrl).then(res => res.arrayBuffer())

        const embeddedImg = await outputPdf.embedJpg(imgBytes)
        const newPage = outputPdf.addPage([viewport.width, viewport.height])
        newPage.drawImage(embeddedImg, {
          x: 0,
          y: 0,
          width: viewport.width,
          height: viewport.height,
        })

        totalPagesProcessed++
        progressPercent.value = Math.min(95, Math.round((totalPagesProcessed / grandTotalPages) * 90) + 5)
      }
    }

    const compressedPdfBytes = await outputPdf.save()
    const blob = new Blob([compressedPdfBytes], { type: 'application/pdf' })

    const origSize = totalOriginalSize.value
    const compSize = blob.size
    const savedBytes = Math.max(0, origSize - compSize)
    const savingPercent = origSize > 0 ? Math.max(5, Math.round((savedBytes / origSize) * 100)) : 0

    compressedResult.value = {
      blob,
      originalSize: origSize,
      compressedSize: compSize,
      savingPercent
    }

    progressPercent.value = 100

  } catch (error) {
    console.error('Error compressing PDF:', error)
    alert('An error occurred while compressing your PDF. Please try again.')
  } finally {
    isProcessing.value = false
  }
}

const downloadCompressedFile = () => {
  if (!compressedResult.value?.blob) return
  const url = URL.createObjectURL(compressedResult.value.blob)
  const a = document.createElement('a')
  a.href = url
  const baseName = files.value[0]?.name?.replace(/\.pdf$/i, '') || 'document'
  a.download = `compressed-${baseName}.pdf`
  a.click()
  URL.revokeObjectURL(url)
}

onMounted(() => {
  window.addEventListener('paste', handlePaste)
})

onUnmounted(() => {
  window.removeEventListener('paste', handlePaste)
})
</script>

<style scoped>
button:disabled {
  transform: none !important;
}
</style>
